// The rendering contract. Feature/UI code mounts a viewport through this
// interface and never touches a concrete engine (DMV today,
// cornerstone3D later). engineSelector picks the implementation per SOP Class.

import type { DicomJson } from '../data/dicomTags';
import type { ViewGeometry } from './align/transform';
import type { ScrollInput } from './scrollZoom';
import type { Window, Windowing } from './window/windowing';
import type { ZoomScale } from './zoomLimits';

export type { ScrollInput, ViewGeometry };

export interface EngineContext {
  /** The underlying dicomweb-client, for engines that fetch their own tiles. */
  client: unknown;
  studyInstanceUID: string;
  seriesInstanceUID: string;
  /** Per-instance series metadata (for WSI, one entry per pyramid level). */
  metadata: DicomJson[];
}

/** Live view state surfaced to the controls overlay. */
export interface ViewState {
  /** Displayed objective magnification (e.g. 20), or null if not derivable. */
  magnification: number | null;
  /** Magnification/scale derived from synthetic (guessed) pixel spacing. */
  magnificationApproximate: boolean;
  /** Physical mm per CSS pixel at the current zoom; null if unknown/synthetic
   *  (drives the scale bar — suppressed when null). */
  mmPerPixel: number | null;
  /** Zoom as a percent of native (base-level 1:1) resolution. */
  zoomPercent: number;
  /** Current rotation in degrees. */
  rotationDeg: number;
  canZoomIn: boolean;
  canZoomOut: boolean;
  /** Current viewport as a fraction of the slide (center + span), for the
   *  overview minimap rectangle. Null if not derivable. */
  viewRect: { x: number; y: number; w: number; h: number } | null;
}

/** A whole-slide raster for image analysis (registration), in DISPLAY
 *  orientation with any TILED_FULL padding removed. */
export interface SlideRaster {
  width: number;
  height: number;
  /** RGBA, row-major, width*height*4. */
  data: Uint8ClampedArray;
  /** mm per pixel OF THIS RASTER, or null when the slide's spacing is synthetic.
   *  This is what lets two slides be brought to a common physical scale. */
  mmPerPx: number | null;
}

/**
 * One segment of a DICOM Segmentation drawn over the slide (an AI model's mask,
 * for one): what the engine calls it, and how the Segmentation names it.
 * `uid` is the engine's handle for showLayer / hideLayer / setLayerStyle.
 */
export interface LayerInfo {
  uid: string;
  /** SegmentNumber, 1-based, as in the Segment Sequence. */
  segmentNumber: number;
  /** SegmentLabel. */
  label: string;
}

/** How a layer is drawn. Unset fields are left as they are. */
export interface LayerStyle {
  /** 0 (invisible) to 1 (opaque). */
  opacity?: number;
  /** 0-255 sRGB, the colour the segment's pixels are painted in. */
  color?: [number, number, number];
}

/** One pyramid level of a slide, in the STORED pixel matrix, for a snapshot. */
export interface TileLevel {
  /** The level's pixel matrix. */
  width: number;
  height: number;
  /** Its frames' size; the last column and row are padded to a whole frame. */
  tileWidth: number;
  tileHeight: number;
  /** Base px per px of this level: 1 at the base, 2 one level up. */
  resolution: number;
}

/**
 * The slide's tiles as the engine shows them, for a snapshot of part of it
 * (viewer/capture): read from the pyramid rather than the screen, so the
 * output can be finer than the pane.
 */
export interface TileSource {
  /** Coarsest first. */
  levels: TileLevel[];
  /**
   * The tiles of one level at the given cells, decoded and shown through the
   * slide's colour table as the canvas shows them (a corrected tile comes as
   * a canvas in the table's gamut; the caller's canvas converts as it draws).
   * Null for a tile that could not be had. One request for the lot.
   */
  tiles(level: number, cells: Array<{ col: number; row: number }>): Promise<(CanvasImageSource | null)[]>;
  /** A base px of the matrix → where it shows, as a [0,1] fraction of the slide as displayed (the minimap's space). */
  displayFraction(x: number, y: number): [number, number];
  /** mm per base px, or null when unknown/synthetic. */
  mmPerPixel: number | null;
  /** Objective magnification at the base level, or null. */
  baseMagnification: number | null;
}

/** Which controls make sense for a given engine. */
export interface EngineCapabilities {
  /** The lens: a magnification readout and presets on its scale (zoomScale). */
  magnification: boolean;
  /** The lens's scale (zoomLimits): a slide's objective magnification when absent. */
  zoomScale?: ZoomScale;
  /** View rotation is supported. */
  rotate: boolean;
  /** Engine renders its own physical scale bar. */
  scaleBar: boolean;
  /** Engine exposes image↔viewport transforms, so annotations can be drawn. */
  annotations: boolean;
  /**
   * Its tiles are pictures the browser decodes: what the snapshot
   * (features/capture), the lens's previews and the guided review's tissue
   * map are made from. False for an X-ray, whose 16-bit tiles only the engine
   * can draw. Absent means true.
   */
  pictureTiles?: boolean;
}

/**
 * Whether a slide's own tools apply to the image an engine shows: the snapshot
 * and the guided review. Both decode its tiles, and both speak objective
 * magnification — a snapshot named "12x", or a scan's record "at 12x", for an
 * X-ray shown at 12% of its pixels would be wrong.
 */
export const slideTools = (caps: Partial<EngineCapabilities> | null | undefined): boolean =>
  caps?.pictureTiles !== false && (caps?.zoomScale?.unit ?? '×') === '×';

export interface ViewportEngine {
  readonly capabilities: EngineCapabilities;

  /** Mount and render into the given container. */
  init(container: HTMLElement, ctx: EngineContext): Promise<void>;
  /** React to a container size change. */
  resize(): void;
  /** Tear down: release listeners and references. */
  destroy(): void;

  zoomIn(): void;
  zoomOut(): void;
  /** Multiply zoom by `factor` (>1 in, <1 out), anchored at client coords if given.
   *  Used for smooth trackpad-pinch zoom. */
  zoomByFactor(factor: number, anchor?: { x: number; y: number }): void;
  /** zoomByFactor anchored at a [0,1] slide fraction rather than a screen
   *  point: the overview minimap zooming about the spot under its pointer. */
  zoomAtFraction(factor: number, x: number, y: number): void;
  /** Zoom from a plain scroll (a wheel, or two fingers; never a pinch), anchored
   *  at client coords if given. The view's one scroll gesture (scrollZoom), from
   *  wherever on the slide it comes. */
  scrollZoom(e: ScrollInput, anchor?: { x: number; y: number }): void;
  /** scrollZoom anchored at a [0,1] slide fraction: the overview minimap. */
  scrollZoomAtFraction(e: ScrollInput, x: number, y: number): void;
  /** Fit the whole image to the viewport (minimum zoom). */
  fit(): void;
  /**
   * Bring a region of the slide into view — an annotation picked from the list:
   * centred on it, zoomed so it fills about 60% of the pane, in or out, but
   * never closer than 100% (engines/showRegion.ts). In base-level image
   * pixels, the annotations' space. No-op if unsupported.
   */
  showImageRegion(region: { minX: number; minY: number; maxX: number; maxY: number }): void;
  /** Set zoom to a fraction t in [0,1] across [minZoom, maxZoom]. */
  zoomToFraction(t: number): void;
  /** Jump to an approximate objective magnification (no-op if unsupported). */
  setMagnification(mag: number): void;
  /** Rotate the view by a delta in degrees (no-op if unsupported). */
  rotateBy(deg: number): void;
  /** Set the absolute view rotation in degrees (0–360, no 90° snap; no-op if
   *  unsupported). Used by the fine rotation control (slider / numeric input). */
  rotateTo(deg: number): void;
  /**
   * A small preview image (object URL) of the region at the current view center
   * rendered at the given magnification — used by the magnification lens on hover.
   * Returns null if unsupported/unavailable. Caller revokes the URL.
   */
  previewAt(magnification: number): Promise<string | null>;
  /** Center the view at the given [0,1] slide fraction (the overview minimap).
   *  `animate` false moves at once: a drag on the minimap sends one per frame. */
  navigateToFraction(x: number, y: number, animate?: boolean): void;

  getViewState(): ViewState | null;
  /** Subscribe to view-state changes; returns an unsubscribe fn. */
  subscribe(cb: (s: ViewState) => void): () => void;

  /** Where this view sits over its slide, in base-level DISPLAY-oriented pixels
   *  (see align/transform.ts for why that space). Null if the engine can't
   *  express one — which is how alignment/compare-lock is gated off for flat
   *  images. */
  getViewGeometry(): ViewGeometry | null;
  /**
   * Place the view exactly, with no animation and NO clamping to this slide's
   * own extent or zoom ladder. The clamps have to go: a follower pane's view is
   * derived from ANOTHER slide's, so it legitimately hangs off the edge near
   * borders, and OpenLayers otherwise quantises the resolution to its discrete
   * ladder — which would break "B sits at exactly scale × A's resolution".
   * No-op if unsupported.
   */
  setViewGeometry(g: Omit<ViewGeometry, 'imageWidth' | 'imageHeight'>): void;
  /**
   * Persistent free-view mode for a pane that is slaved to another one. A
   * one-shot bypass inside setViewGeometry is not enough: OpenLayers re-applies
   * its centre and resolution constraints on every container resize
   * (Map#updateSize → View#setViewportSize → resolveConstraints), which would
   * silently yank a follower back inside its own extent. No-op if unsupported.
   */
  setFreeView(enabled: boolean): void;

  /** ICC color-management state. `available` is false when the slide needs no
   *  correction (no profile, or an sRGB one); `enabled` is whether correction is
   *  currently applied; `name` is the profile's description (e.g. "Segue_v3");
   *  `unavailable` means the slide HAS a profile that could not be applied, so it
   *  is shown in its raw colour and the viewer should say so. */
  colorManaged(): { available: boolean; enabled: boolean; name?: string; nameLoading: boolean; unavailable?: boolean };
  /** Enable/disable ICC color correction (no-op if unavailable). */
  setColorManaged(enabled: boolean): void;

  /** Base-level image pixel → viewport CSS px (from the viewport top-left),
   *  accounting for live zoom/pan/rotation. Null if unavailable. */
  imageToViewport(x: number, y: number): { x: number; y: number } | null;
  /** Viewport CSS px → base-level image pixel. Null if unavailable. */
  viewportToImage(x: number, y: number): { x: number; y: number } | null;
  /** mm per base-level image pixel, or null when unknown/synthetic (show px). */
  pixelSpacingMm(): number | null;
  /**
   * The whole slide as one small raster, for analysis rather than display: the
   * cheapest pyramid level that still covers it, decoded, unpadded and rotated
   * into display orientation. Null when the engine can't produce one.
   */
  slideRaster(): Promise<SlideRaster | null>;
  /** Subscribe to map paints — fires once per rendered frame (each zoom,
   *  pan, rotate or resize frame, and each tile that lands), and nothing
   *  while idle. Fires inside the engine's own animation frame, after the
   *  canvas is drawn and before the browser composites: what a subscriber
   *  puts in the DOM before returning lands in the same frame as the slide.
   *  Whether the view moved is the subscriber's to tell, from
   *  imageToViewport. Returns an unsubscribe fn. */
  onRender(cb: () => void): () => void;
  /**
   * Fires once, when the engine has painted a COMPLETE view — every tile the
   * current viewport needs is loaded and drawn, not merely "the map exists".
   * That is the moment the slide backdrop can be retired without exposing
   * half-loaded tiles. Returns an unsubscribe fn.
   *
   * Optional, but implement it if the engine can possibly tell: an engine that
   * leaves it out makes the viewport fall back to a timer, which means a
   * blurred stand-in sits over an image that may already be finished.
   */
  onFirstPaint?(cb: () => void): () => void;
  /**
   * Whether the map is waiting on tiles for the current view: `true` on the
   * first painted frame that leaves tiles pending, `false` on the first that
   * leaves none (engines/loading.ts has OpenLayers' exact rules). The review
   * scan holds its sweep on it so nobody reviews blank tiles. Optional: an
   * engine without it is taken to be always ready.
   */
  onLoading?(cb: (loading: boolean) => void): () => void;
  /**
   * Whether a view animation is running or pending: a wheel notch's glide,
   * the minimap's 200 ms pan, a zoom key, the lens, a kinetic pan after a
   * drag. The review scan never animates through the engine, so to it any
   * animation is somebody else's intent, and it must see one BEFORE its own
   * placement cancels it. Optional: without it, animated moves that have not
   * yet changed the view go unnoticed.
   */
  isAnimating?(): boolean;
  /** Cancel every view animation, finishing the engine's own at their targets. Optional. */
  cancelAnimations?(): void;
  /** The map's own size in CSS px, before any rotation. Optional. */
  getViewportSize?(): { w: number; h: number } | null;
  /**
   * Fetch the tiles for a view about to be shown (the review scan's next
   * screen), after the visible ones; null stops. One view at a time: setting
   * another replaces it. Optional: without it the scan simply loads as it goes.
   */
  preloadAt?(g: Omit<ViewGeometry, 'imageWidth' | 'imageHeight'> | null): void;
  /**
   * The review scan started or stopped playing. While it plays the engine
   * keeps the light level (it paces itself on the map's loading), and may
   * sharpen again once it pauses (stillSharp). Optional.
   */
  setScanPlaying?(on: boolean): void;
  /**
   * The slide's tiles for a snapshot (features/capture), null before the
   * slide is open. Optional: an engine without it offers no snapshot.
   */
  tileSource?(): TileSource | null;

  // ── Window/level (an X-ray). Optional: an engine without them, or an image
  //    with nothing to window, shows no window controls. ──

  /** What the image offers to window: its file's windows and its range; null for colour or 8-bit. */
  windowing?(): Windowing | null;
  /** Show the image through this window, turned over when `inverted`. Drawn on the GPU: nothing is fetched again. */
  setWindow?(window: Window, inverted: boolean): void;

  // ── Overlay layers (AI masks). Optional: an engine without them shows the
  //    results but no layer controls. ──

  /**
   * Add the segments of one Segmentation series over the slide, hidden: one
   * entry per segment. `seriesMetadata` is the series' WADO metadata (its
   * instances); its frames are fetched through the engine's own DICOMweb
   * client, a segment per request. Adding a series already added answers
   * with its layers again.
   */
  addSegmentationLayer?(seriesMetadata: DicomJson[]): Promise<LayerInfo[]>;
  /**
   * Fetch the pixels of an added Segmentation series' layers now, before any
   * is shown, so showing one draws at once. Kept until the layers are removed
   * or the engine is destroyed.
   */
  prefetchLayers?(seriesUid: string): void;
  showLayer?(uid: string): void;
  hideLayer?(uid: string): void;
  setLayerStyle?(uid: string, style: LayerStyle): void;
  /** Take every layer of one Segmentation series off the slide. */
  removeLayers?(seriesUid: string): void;
}
